import { useEffect, useState } from 'react';
import { callUIAPI } from '@utils/apiUtils';

interface ExistingCompany {
  id: number;
  name?: string;
  createdOn?: string;
}

/**
 * The 409 that catches a duplicate onboarding carries the company's id and name
 * and nothing else. The interstitial wants to say when that workspace was set
 * up, so it reads the company itself. A failed read costs the date line and
 * nothing more — the screen still names the company and opens the workspace.
 */
export function useExistingCompany(companyId: number | null): ExistingCompany | null {
  const [company, setCompany] = useState<ExistingCompany | null>(null);

  useEffect(() => {
    if (!companyId) {
      setCompany(null);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await callUIAPI(`companies/${companyId}`, null, 'get', true) as
          { data?: { item?: ExistingCompany } } | undefined;
        const item = response?.data?.item;
        if (!cancelled && item) setCompany(item);
      } catch {
        // The date line is an enhancement; the screen works without it.
      }
    })();

    return () => { cancelled = true; };
  }, [companyId]);

  return company;
}
